import { BKJButton } from '@bookjane2/bookjane-design-library';
import { Flex } from 'components/Flex';
import { DeleteButton } from 'modals/ShiftModal/src/views/DetailView/DetailTabView/DetailTabView.styled';
import { Fragment } from 'react';
import { useTranslation } from 'hooks/useTranslation';

import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import { isUserReadOnlySelector } from 'store/selectors/userSelectors/isUserReadOnlySelector';
import { UserGuard } from 'guards/UserGuard';
import { UserPermissionEnum } from 'guards/UserGuard.types';
import { useSelector } from 'react-redux';
import { IAuditTrailFooterProps } from 'modals/ShiftModal/src/views/DetailView/AuditTrailDetailView/AuditTrail.types';
import { isEditCompletedShiftsFeatureEnabledSelector } from 'store/selectors/featureFlagSelectors';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import { OrderStatusEnum } from 'types/graphql';
import { UnableToEditMessage } from 'modals/ShiftModal/src/views/DetailView/UnableToEditMessage';
import { FEATURE_SELECTORS_MAP } from 'guards/FeatureGuard.constants';
import { DeleteBlockDialog } from 'components/DeleteBlockDialog';
import { useShiftModalPropsContext } from 'modals/ShiftModal/ShiftModal.props.context';
import { shiftModalRoutingService } from 'modals/ShiftModal/ShiftModal.ui.context';

export function WebAuditTrailDetailTabViewFooter(props: IAuditTrailFooterProps) {
  const { t } = useTranslation();
  const { refreshBackgroundPage } = useShiftModalPropsContext();

  const {
    order,
    goToDeleteView,
    goToEditView,
    goToReleaseShiftView,
    goToSendToAgenciesView,
    goToAwardTeamMemberView,
    validateAndExecute,
    biddingStatus,
    isBlockShift,
    isBlockDeleteConfirmationOpen,
    setIsBlockDeleteConfirmationOpen,
  } = props;

  const { value: locationId } = getCurrentlySelectedLocation();

  const isEditCompletedShiftsFeatureEnabled = useSelector(
    isEditCompletedShiftsFeatureEnabledSelector(Number.parseInt(locationId)),
  );

  const isCompletedShiftNotEditable =
    order.status?.value === OrderStatusEnum.completed &&
    !order.isEditable &&
    isEditCompletedShiftsFeatureEnabled;

  const isYMCAReadOnly = useSelector(
    FEATURE_SELECTORS_MAP['FEATURE_TYPE_YMCA_READ_ONLY_SECONDARY_USER'],
  );
  const isSecondaryUserReadOnly = useSelector(isUserReadOnlySelector);
  const isReadOnly = isYMCAReadOnly || isSecondaryUserReadOnly;

  if (isReadOnly) return <Fragment />;
  return (
    <BKJSideModalFooter>
      <Flex justifyContent="space-between" width="100%">
        <UserGuard
          userType={[
            'USER_TYPE_B2B',
            'USER_TYPE_PREMIUM_SECONDARY',
            'USER_TYPE_ESSENTIAL_SECONDARY',
          ]}
        >
          {!!order.isDeletable && (
            <Flex>
              <DeleteButton
                variant="RedText"
                onClick={
                  isBlockShift ? () => setIsBlockDeleteConfirmationOpen(true) : goToDeleteView
                }
              >
                {t(isBlockShift ? 'shift_modal:DELETE_BLOCK' : 'shift_modal:DELETE')}
              </DeleteButton>
            </Flex>
          )}
          {isBlockShift && !!order.shiftBlocks?.nodes?.[0]?.id && (
            <DeleteBlockDialog
              isOpen={isBlockDeleteConfirmationOpen}
              setIsOpen={setIsBlockDeleteConfirmationOpen}
              blockId={order.shiftBlocks.nodes[0].id}
              onSuccess={() => {
                refreshBackgroundPage();
                shiftModalRoutingService.close();
              }}
            />
          )}
        </UserGuard>

        <Flex width="100%" justifyContent="flex-end" gap="8px">
          <UnableToEditMessage />
          {(!!order.isEditable || isCompletedShiftNotEditable) && (
            <BKJButton
              width="180px"
              variant="PurpleOutlined"
              onClick={goToEditView}
              disabled={!order.isEditable}
            >
              {t('schedule:EDIT')}
            </BKJButton>
          )}
          {order.isJaneReleaseAllowed && order.status?.value !== OrderStatusEnum.in_progress && (
            <BKJButton width="180px" variant="PurpleSolid" onClick={goToReleaseShiftView}>
              {t('shift_modal:RELEASE')}
            </BKJButton>
          )}
          <UserGuard
            userType={[
              UserPermissionEnum.USER_TYPE_B2B,
              UserPermissionEnum.USER_TYPE_PREMIUM_PRIMARY,
              UserPermissionEnum.USER_TYPE_PREMIUM_SECONDARY,
            ]}
          >
            {order.canSendToAgency && !order.biddable && (
              <BKJButton width="185px" variant="PurpleSolid" onClick={goToSendToAgenciesView}>
                {t('shift_modal:SEND_TO_AGENCIES')}
              </BKJButton>
            )}
          </UserGuard>
          <UserGuard
            userType={[
              'USER_TYPE_B2B',
              'USER_TYPE_PREMIUM_SECONDARY',
              'USER_TYPE_ESSENTIAL_SECONDARY',
            ]}
          >
            {order.canB2bAssign && (
              <BKJButton width="180px" variant="PurpleSolid" onClick={validateAndExecute}>
                {t('shift_modal:ASSIGN')}
              </BKJButton>
            )}
          </UserGuard>
          <UserGuard
            userType={[
              UserPermissionEnum.USER_TYPE_AGENCY,
              UserPermissionEnum.USER_TYPE_AGENCY_PRIMARY,
              UserPermissionEnum.USER_TYPE_AGENCY_SECONDARY,
            ]}
          >
            {order.canAssign && (
              <BKJButton width="180px" variant="PurpleSolid" onClick={validateAndExecute}>
                {t('shift_modal:ASSIGN')}
              </BKJButton>
            )}
          </UserGuard>
          <UserGuard
            userType={[
              UserPermissionEnum.USER_TYPE_B2B,
              UserPermissionEnum.USER_TYPE_PREMIUM_PRIMARY,
              UserPermissionEnum.USER_TYPE_PREMIUM_SECONDARY,
            ]}
          >
            {order.biddable && biddingStatus === 'AWARDING' && (
              <BKJButton width="180px" variant="PurpleSolid" onClick={goToAwardTeamMemberView}>
                {order.isBlockShift ? t('shift_modal:AWARD_BLOCK') : t('shift_modal:AWARD_SHIFT')}
              </BKJButton>
            )}
          </UserGuard>
        </Flex>
      </Flex>
    </BKJSideModalFooter>
  );
}
